import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../lib/api";
import { Tag, Percent, Calendar, TrendingUp, ShoppingBag, Check, Copy } from "lucide-react";
import { toast } from "sonner";

/**
 * /innstillinger/kampanjekoder – Bruker-side for kampanje-kode-historikk.
 * - Fane 1: Mine koder (når jeg er tildelt som eier / influencer)
 * - Fane 2: Brukshistorikk (kjøp jeg har gjort med kampanje-kode)
 */
export default function KampanjekoderPage() {
  const { t, i18n } = useTranslation();
  const [tab, setTab] = useState("owned"); // owned | history
  const [owned, setOwned] = useState({ items: [], total: 0 });
  const [history, setHistory] = useState({ items: [], total: 0, total_saved_nok: 0 });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get("/me/campaign-codes/owned"),
        api.get("/me/campaign-codes/history"),
      ]);
      setOwned(r1.data || { items: [], total: 0 });
      setHistory(r2.data || { items: [], total: 0, total_saved_nok: 0 });
    } catch (e) {
      // stille feil – tomme lister
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const copy = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(t("kampanjekoder.copied", { code }));
  };

  const localeMap = { no: "nb-NO", sv: "sv-SE", da: "da-DK", en: "en-GB" };
  const dateLocale = localeMap[i18n.language] || "nb-NO";

  return (
    <div className="space-y-8" data-testid="settings-kampanjekoder">
        <header>
          <div className="font-meta text-[10px] tracking-[0.3em] text-[var(--magenta)] mb-1 flex items-center gap-2">
            <Tag className="h-3 w-3" /> {t("kampanjekoder.section_label")}
          </div>
          <h2 className="font-fraunces italic text-3xl text-[var(--cream)]">
            {t("kampanjekoder.title")}
          </h2>
          <p className="text-sm text-[var(--cream-soft)] mt-2">
            {t("kampanjekoder.intro")}
          </p>
        </header>

        {/* Tab-bar */}
        <div className="flex border-b border-white/10">
          <button
            onClick={() => setTab("owned")}
            className={`px-4 py-3 font-meta border-b-2 transition-colors ${
              tab === "owned"
                ? "border-[var(--magenta)] text-[var(--cream)]"
                : "border-transparent text-[var(--cream-soft)] hover:text-[var(--cream)]"
            }`}
            data-testid="tab-owned"
          >
            {t("kampanjekoder.tab_owned")} ({owned.total})
          </button>
          <button
            onClick={() => setTab("history")}
            className={`px-4 py-3 font-meta border-b-2 transition-colors ${
              tab === "history"
                ? "border-[var(--magenta)] text-[var(--cream)]"
                : "border-transparent text-[var(--cream-soft)] hover:text-[var(--cream)]"
            }`}
            data-testid="tab-history"
          >
            {t("kampanjekoder.tab_history")} ({history.total})
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12 text-[var(--cream-soft)] italic font-fraunces">
            {t("kampanjekoder.loading")}
          </div>
        ) : tab === "owned" ? (
          <OwnedTab items={owned.items} onCopy={copy} t={t} dateLocale={dateLocale} />
        ) : (
          <HistoryTab items={history.items} totalSaved={history.total_saved_nok} t={t} dateLocale={dateLocale} />
        )}
      </div>
  );
}

function OwnedTab({ items, onCopy, t, dateLocale }) {
  if (items.length === 0) {
    return (
      <div className="editorial-card p-8 text-center">
        <Tag className="h-8 w-8 mx-auto mb-3 text-[var(--cream-faint)]" />
        <p className="font-fraunces italic text-[var(--cream-soft)] mb-2">
          {t("kampanjekoder.no_owned_title")}
        </p>
        <p className="font-manrope text-xs text-[var(--cream-faint)] max-w-md mx-auto">
          {t("kampanjekoder.no_owned_desc")}
        </p>
      </div>
    );
  }

  const totalUses = items.reduce((acc, c) => acc + (c.use_count || 0), 0);
  const totalGenerated = items.reduce((acc, c) => acc + (c.total_base_amount || 0), 0);

  return (
    <div className="space-y-4" data-testid="owned-codes-list">
      {/* Sammenfatning */}
      <div className="grid grid-cols-3 gap-4">
        <div className="editorial-card p-4">
          <div className="font-meta">{t("kampanjekoder.kpi_active")}</div>
          <div className="font-fraunces text-3xl text-[var(--cream)]">
            {items.filter((c) => c.currently_valid).length}
          </div>
        </div>
        <div className="editorial-card p-4">
          <div className="font-meta">{t("kampanjekoder.kpi_total_uses")}</div>
          <div className="font-fraunces text-3xl text-[var(--magenta)]">{totalUses}</div>
        </div>
        <div className="editorial-card p-4">
          <div className="font-meta">{t("kampanjekoder.kpi_revenue")}</div>
          <div className="font-fraunces text-3xl text-[var(--cream)]">
            {totalGenerated.toFixed(0)} <span className="text-base text-[var(--cream-soft)]">kr</span>
          </div>
        </div>
      </div>

      {/* Liste */}
      {items.map((c) => {
        const usePct = c.max_uses ? Math.round(((c.use_count || 0) / c.max_uses) * 100) : null;
        const status = !c.active
          ? { label: t("kampanjekoder.status_deactivated"), color: "text-zinc-500" }
          : !c.currently_valid
          ? { label: t("kampanjekoder.status_inactive"), color: "text-amber-500" }
          : { label: t("kampanjekoder.status_active"), color: "text-emerald-500" };
        const appliesLabel = c.applies_to === "chips"
          ? t("kampanjekoder.applies_chips")
          : c.applies_to === "vip"
          ? t("kampanjekoder.applies_vip")
          : t("kampanjekoder.applies_all");
        return (
          <div key={c.id} className="editorial-card p-5" data-testid={`owned-code-${c.code}`}>
            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono text-xl text-[var(--cream)] tracking-wider">
                    {c.code}
                  </span>
                  <button
                    onClick={() => onCopy(c.code)}
                    className="text-[var(--cream-soft)] hover:text-[var(--magenta)] transition-colors"
                    title={t("kampanjekoder.copy")}
                    data-testid={`copy-code-${c.code}`}
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                  <span className={`font-meta ${status.color}`}>{status.label}</span>
                </div>
                {c.note && (
                  <p className="text-xs italic text-[var(--cream-faint)]">{c.note}</p>
                )}
              </div>
              <div className="text-right">
                <div className="text-[var(--magenta)] inline-flex items-center gap-1 font-fraunces text-2xl">
                  <Percent className="h-4 w-4" />
                  {c.discount_pct}
                </div>
                <div className="font-meta uppercase tracking-wider text-[10px] text-[var(--cream-soft)]">
                  {appliesLabel}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <div>
                <div className="font-meta">{t("kampanjekoder.used")}</div>
                <div className="font-fraunces text-[var(--cream)]">
                  {c.use_count}
                  {c.max_uses && <span className="text-[var(--cream-faint)]"> / {c.max_uses}</span>}
                </div>
                {usePct != null && (
                  <div className="mt-1 h-1 bg-white/5 rounded">
                    <div
                      className="h-1 bg-[var(--magenta)] rounded"
                      style={{ width: `${Math.min(100, usePct)}%` }}
                    />
                  </div>
                )}
              </div>
              <div>
                <div className="font-meta">{t("kampanjekoder.discount_given")}</div>
                <div className="font-fraunces text-[var(--cream)]">
                  {c.total_discount_given.toFixed(0)} kr
                </div>
              </div>
              <div>
                <div className="font-meta">{t("kampanjekoder.revenue")}</div>
                <div className="font-fraunces text-[var(--cream)]">
                  {c.total_base_amount.toFixed(0)} kr
                </div>
              </div>
              {c.valid_until && (
                <div>
                  <div className="font-meta inline-flex items-center gap-1">
                    <Calendar className="h-3 w-3" /> {t("kampanjekoder.expires")}
                  </div>
                  <div className="font-fraunces text-[var(--cream)]">
                    {new Date(c.valid_until).toLocaleDateString(dateLocale)}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function HistoryTab({ items, totalSaved, t, dateLocale }) {
  if (items.length === 0) {
    return (
      <div className="editorial-card p-8 text-center">
        <ShoppingBag className="h-8 w-8 mx-auto mb-3 text-[var(--cream-faint)]" />
        <p className="font-fraunces italic text-[var(--cream-soft)] mb-2">
          {t("kampanjekoder.no_history_title")}
        </p>
        <p className="font-manrope text-xs text-[var(--cream-faint)]">
          {t("kampanjekoder.no_history_desc")}
        </p>
      </div>
    );
  }

  const methodLabel = (m) => {
    if (m === "card") return t("kampanjekoder.method_card");
    if (m === "crypto") return t("kampanjekoder.method_crypto");
    return t("kampanjekoder.method_strex");
  };

  return (
    <div className="space-y-4" data-testid="history-codes-list">
      <div className="editorial-card p-5 bg-[var(--magenta)]/5 border-[var(--magenta)]/30">
        <div className="flex items-center gap-3">
          <TrendingUp className="h-6 w-6 text-[var(--magenta)]" />
          <div>
            <div className="font-meta">{t("kampanjekoder.total_saved")}</div>
            <div className="font-fraunces italic text-3xl text-[var(--magenta)]">
              {totalSaved.toFixed(0)} kr
            </div>
          </div>
        </div>
      </div>

      {items.map((p) => (
        <div key={p.purchase_id} className="editorial-card p-4 flex flex-wrap items-center gap-4" data-testid={`history-purchase-${p.purchase_id}`}>
          <div className="flex-1 min-w-[200px]">
            <div className="flex items-center gap-2">
              <span className="font-mono text-[var(--cream)]">{p.code}</span>
              {p.status === "completed" && (
                <span className="text-emerald-500 inline-flex items-center gap-1 font-meta">
                  <Check className="h-3 w-3" /> {t("kampanjekoder.completed")}
                </span>
              )}
            </div>
            <div className="text-xs text-[var(--cream-soft)] mt-1">
              {p.tokens} {t("kampanjekoder.chips_label")} · {methodLabel(p.method)}
            </div>
          </div>
          <div className="text-right">
            <div className="font-meta">{t("kampanjekoder.you_saved")}</div>
            <div className="font-fraunces text-2xl text-[var(--magenta)]">
              {(p.discount_amount || 0).toFixed(0)} kr
            </div>
            <div className="text-[10px] text-[var(--cream-faint)] line-through">
              {(p.list_price || 0).toFixed(0)} kr → {(p.final_price || 0).toFixed(0)} kr
            </div>
          </div>
          <div className="text-xs text-[var(--cream-faint)] w-full sm:w-auto">
            {new Date(p.created_at).toLocaleDateString(dateLocale)}
          </div>
        </div>
      ))}
    </div>
  );
}
